import React, { useState } from 'react';

interface EnterpriseContactFormProps {
  visible: boolean;
  onClose: () => void;
}

const salesTeam = [
  // Replace with your actual avatar URLs or use placeholder images
  'https://randomuser.me/api/portraits/men/32.jpg',
  'https://randomuser.me/api/portraits/women/44.jpg',
  'https://randomuser.me/api/portraits/men/45.jpg',
  'https://randomuser.me/api/portraits/women/46.jpg',
  'https://randomuser.me/api/portraits/men/47.jpg',
  'https://randomuser.me/api/portraits/women/48.jpg',
];

const EnterpriseContactForm: React.FC<EnterpriseContactFormProps> = ({ visible, onClose }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    info: '',
  });
  const [submitted, setSubmitted] = useState(false);

  if (!visible) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    // Here you could send the data to your backend
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl flex flex-col md:flex-row overflow-hidden">
        <div className="flex-1 p-8">
          {submitted ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="text-3xl mb-4">🎉</div>
              <h2 className="text-xl font-bold mb-2">Thank you!</h2>
              <p className="text-gray-600 mb-6 text-center">Our sales team will get back to you shortly.</p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-[#0078d4] text-white rounded hover:bg-[#106ebe] focus:outline-none focus:ring-2 focus:ring-[#0078d4] focus:ring-offset-2"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <h2 className="text-2xl font-bold mb-2">Let's talk</h2>
              <p className="text-gray-600 mb-6">Tell us about yourself and we'll get back to you shortly.</p>
              <div>
                <label className="block text-sm font-medium mb-1">Full name</label>
                <input
                  name="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Business email</label>
                <input
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone number <span className="text-gray-400">(Optional)</span></label>
                <input
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+1-XXX XXX XXXX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 flex items-center">
                  Additional information
                  <span className="ml-1 text-gray-400 cursor-pointer" title="Tell us more about your industry, challenges, needs">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" /></svg>
                  </span>
                </label>
                <textarea
                  name="info"
                  rows={3}
                  value={form.info}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Tell us more about your industry, challenges, needs"
                />
              </div>
              <button
                type="submit"
                className="w-full py-3 bg-[#0078d4] text-white rounded font-semibold hover:bg-[#106ebe] focus:outline-none focus:ring-2 focus:ring-[#0078d4] focus:ring-offset-2 transition-colors"
              >
                Request a call
              </button>
            </form>
          )}
        </div>
        <div className="hidden md:flex flex-col justify-between bg-gray-50 p-8 w-96 border-l border-gray-100">
          <div>
            <h3 className="text-lg font-semibold mb-4">What can you expect?</h3>
            <ul className="space-y-4 text-gray-700 text-sm">
              <li className="flex items-start"><span className="mr-2 mt-1">✔️</span>Discuss your use case and individual needs with our Sales Team</li>
              <li className="flex items-start"><span className="mr-2 mt-1">✔️</span>Discover how our products can strengthen your customer relationships and increase revenue</li>
              <li className="flex items-start"><span className="mr-2 mt-1">✔️</span>Find out how others in your industry are leveraging Scribedo</li>
            </ul>
          </div>
          <div className="mt-8">
            <div className="text-xs text-gray-500 mb-2">Our Sales Team is ready to help you</div>
            <div className="flex -space-x-2">
              {salesTeam.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt="Sales Team Member"
                  className="w-9 h-9 rounded-full border-2 border-white shadow"
                />
              ))}
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none"
          aria-label="Close"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default EnterpriseContactForm; 